import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { useParams } from "@/hooks/useSearchParams";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useNavigate } from "@/router";
import { MEMBER_ELECTIONS_PAST_PATH } from "../constants";
import { buildMemberElectionPersonPath } from "../_utils/buildMemberElectionPersonPath";
import { usePastMemberElectionRuns } from "../_hooks/usePastMemberElectionRuns";
import type { MemberElectionRunNode } from "../types";
import PastMemberElectionRunsTable from "./PastMemberElectionRunsTable";

function syncSelectedRunCache(
  cache: Record<string, MemberElectionRunNode>,
  runs: MemberElectionRunNode[],
  rowSelection: Record<string, boolean>
) {
  let changed = false;

  for (const run of runs) {
    if (rowSelection[run.id] && cache[run.id] !== run) {
      cache[run.id] = run;
      changed = true;
    }
  }

  for (const id of Object.keys(cache)) {
    if (!rowSelection[id]) {
      delete cache[id];
      changed = true;
    }
  }

  return changed;
}

export default function PastMemberElectionsContent() {
  const navigate = useNavigate();
  const { searchParamsUrl } = useParams();
  const bulkSelection = useBulkSelection();
  const { Menu, setMenu } = useTableRowMenu();
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);
  const { rowSelection, rowSelectionCount, isSelectedOnly, deselectAll } = bulkSelection;
  const { runs, loading, showLoadError, createHeaderPagination, createFooterPagination } = usePastMemberElectionRuns();
  const cacheRef = useRef<Record<string, MemberElectionRunNode>>({});
  const selectedRunByIdRef = useRef<Record<string, MemberElectionRunNode>>({});

  const selectedRunById = useMemo(() => {
    const changed = syncSelectedRunCache(cacheRef.current, runs, rowSelection);
    if (!changed) {
      return selectedRunByIdRef.current;
    }

    const next = { ...cacheRef.current };
    selectedRunByIdRef.current = next;
    return next;
  }, [runs, rowSelection]);

  useEffect(() => {
    if (rowSelectionCount === 0 && isSelectedOnly) {
      deselectAll();
    }
  }, [rowSelectionCount, isSelectedOnly, deselectAll]);

  const openPersonSideSheet = useCallback((pgaId: string) => {
    setSelectedPgaId(pgaId);
    setIsPersonSideSheetOpen(true);
  }, []);

  const closePersonSideSheet = useCallback(() => {
    setIsPersonSideSheetOpen(false);
    setSelectedPgaId(undefined);
  }, []);

  return (
    <>
      <Menu />
      <div className="w-full pt-12">
        <p className="body-medium text-sys-brand-on-surface-variant">
          Review prior finalize-election batches, including who initiated each run, how many applications were
          submitted, and the overall outcome.
        </p>
        <PastMemberElectionRunsTable
          loading={loading}
          runs={runs}
          selectedRunById={selectedRunById}
          showLoadError={showLoadError}
          bulkSelection={bulkSelection}
          createHeaderPagination={createHeaderPagination}
          createFooterPagination={createFooterPagination}
          setMenu={setMenu}
          onViewPerson={openPersonSideSheet}
        />
      </div>
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={closePersonSideSheet}
        pgaId={selectedPgaId ?? null}
        showEmployment={true}
        showFacility={true}
        customActionButtons={
          selectedPgaId
            ? [
                {
                  text: "View Person",
                  variant: "outlined" as const,
                  onClick: () => {
                    const personPath = buildMemberElectionPersonPath(
                      selectedPgaId,
                      searchParamsUrl,
                      false,
                      MEMBER_ELECTIONS_PAST_PATH
                    );
                    if (personPath) {
                      navigate(personPath);
                    }
                  }
                }
              ]
            : undefined
        }
      />
    </>
  );
}
